"use client";

import { format, getMinutes, setMinutes, getHours, setHours } from "date-fns";
import { Calendar, CalendarProps } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, isDefined } from "@/lib/utils";
import { LuCalendar, LuX } from "react-icons/lu";
import { OnSelectHandler, PropsSingle } from "react-day-picker";
import React from "react";
import { TimePicker, TimePickerProps } from "./time-picker-alt";
import { Button, ButtonProps } from "../custom/button";
import { TZDateMini } from "@date-fns/tz";
import { DebouncedInput, DebouncedInputProps } from "../custom/debounced-input";
import { Indicator } from "../custom/indicator";
import { InputProps } from "./input";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { functionalUpdate } from "@tanstack/react-table";

type Props = Omit<CalendarProps & PropsSingle, "mode">;
export type DatePickerProps = Omit<Props, "selected" | "onSelect"> & {
  value?: Date | null | undefined;
  onChange?: (
    ...event: any[]
  ) => void | OnSelectHandler<Date | null | undefined>;
  selected?: Date | null | undefined;
  onSelect?: OnSelectHandler<Date | null | undefined>;
  button?: ButtonProps;
  input?: InputProps;
  isActive?: boolean;
  inline?: boolean;
  showValue?: boolean;
  placeholder?: string;
  clearable?: boolean;
  isDisabled?: boolean;
  onClose?: () => void;
  id?: string;
} & {
  renderTime?: (date: Date) => string | undefined;
  parseRenderedTime?: (date: string) => Date | null | undefined;
} & Omit<TimePickerProps, "selected" | "onSelect" | "timeZone"> & {
    children?: any;
  };

const retainHoursMinutes = (date?: Date, selected?: Date | null) => {
  if (!date || !selected) return date;
  return setHours(setMinutes(date, getMinutes(selected)), getHours(selected));
};
const chainRetainHoursMinutes =
  (
    onSelect: OnSelectHandler<Date | undefined>,
    selected?: Date | null,
  ): OnSelectHandler<Date | undefined> =>
  (..._args) => {
    const [date, ...args] = _args;
    return onSelect(retainHoursMinutes(date, selected), ...args);
  };

export const DateInput = ({
  selected,
  onSelect,
  renderTime,
  parseRenderedTime,
  timeZone,
  ...props
}: Pick<DatePickerProps, "selected" | "onSelect" | "timeZone"> &
  Required<Pick<DatePickerProps, "renderTime" | "parseRenderedTime">> &
  Omit<
    DebouncedInputProps & {
      type: "string";
      isUndefinable: false;
      isNullable: false;
    },
    | "value"
    | "onChange"
    | "selected"
    | "onSelect"
    | "isUndefinable"
    | "isNullable"
    | "type"
  >) => {
  const value = React.useMemo(
    () => (selected ? `${selected ? renderTime(selected) : ""}` : ""),
    [selected],
  );
  const [state, setState] = React.useState({
    error: false,
    last: selected,
    pending: undefined as string | undefined,
  });
  React.useMemo(
    () => setState((s) => ({ ...s, error: false, last: selected })),
    [setState, selected],
  );
  const onChange = React.useCallback(
    (value: string) => {
      const trimmed = value.trim();
      const parsed = trimmed ? parseRenderedTime(trimmed) || false : undefined;
      const date =
        parsed && timeZone ? new TZDateMini(parsed, timeZone) : parsed;
      if (date === false) {
        setState((s) => ({ ...s, error: true }));
      } else {
        if (onSelect) onSelect(date, new Date(), {}, undefined as any);
        setState((s) => ({ ...s, error: false, last: date }));
      }
    },
    [parseRenderedTime, timeZone],
  );
  const pendingRef = useCurrentRef(state.pending);
  const onChangeRef = useCurrentRef(onChange);
  React.useEffect(
    () => () => {
      if (pendingRef.current) {
        onChangeRef.current(pendingRef.current);
      }
    },
    [],
  );
  return (
    <>
      <DebouncedInput
        tabIndex={0}
        debounce={-1}
        {...props}
        className={cn(
          "border-0 ring-0 outline-none focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-none shadow-none",
          state?.error &&
            "focus-visible:ring-1 ring-1 ring-error focus-visible:ring-error",
          props.className,
        )}
        type={"string"}
        isNullable={false}
        isUndefinable={false}
        value={value}
        onChange={onChange}
        onPending={(pending) => setState((s) => ({ ...s, pending }))}
        onKeyDown={(ev) => {
          if (props.onKeyDown) {
            props.onKeyDown(ev);
          }
          if (state.pending && ev.key == "Enter") {
            ev.preventDefault();
            ev.stopPropagation();
          } else if (onSelect && ev.key === "Escape" && state?.error) {
            ev.preventDefault();
            ev.stopPropagation();
            onSelect(state.last, new Date(), {}, ev);
          }
          setState((s) => ({ ...s, error: false }));
        }}
      />
      {(state.pending !== undefined || state.error) && (
        <span className="ml-auto flex items-center">
          {state.pending !== undefined ? (
            "*"
          ) : state.error ? (
            <Indicator
              size={"2xs"}
              border={"none"}
              variant={"error"}
              className="ml-1.5"
              onClick={(ev) => {
                if (onSelect) onSelect(state.last, new Date(), {}, ev);
                setState((s) => ({ ...s, error: false }));
              }}
            />
          ) : (
            ""
          )}
        </span>
      )}
    </>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const defaultRenderType = (date) => format(date, "dd/MM/yyyy HH:mm:ss");

export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      className,
      selected: __selected,
      onSelect: _onSelect,
      value,
      onChange,
      renderTime = defaultRenderType,
      children,
      defaultMonth: _defaultMonth,

      button,
      input,

      // time-picker
      intervals,
      withAmPm,

      isActive,
      inline,
      showValue = true,

      isDisabled,
      clearable,
      placeholder,
      onClose,

      id,

      ...props
    },
    ref,
  ) => {
    const [open, _setOpen] = React.useState(false);
    const setOpen: typeof _setOpen = React.useCallback(
      (a) =>
        _setOpen((open) => {
          const next = functionalUpdate(a, open);
          if (!next) {
            if (onClose) onClose();
          }
          return next;
        }),

      [onClose, _setOpen],
    );
    const _selected = __selected ?? value;
    const selected = React.useMemo(
      () =>
        _selected && props?.timeZone
          ? new TZDateMini(_selected, props.timeZone)
          : _selected,
      [_selected, props.timeZone],
    );
    const defaultMonth = _defaultMonth ?? (selected || undefined);
    const onSelect = (_onSelect ?? onChange) as OnSelectHandler<
      Date | null | undefined
    >;
    return (
      <Popover
        open={isDisabled ? false : open}
        onOpenChange={isDisabled ? undefined : setOpen}
      >
        <PopoverTrigger disabled={isDisabled} asChild>
          {isDisabled && inline ? (
            <span className={cn("pt-[1px]", className)}>
              {selected ? renderTime(selected) : (placeholder ?? "<N/A>")}
            </span>
          ) : (
            <Button
              {...button}
              id={id ?? button?.id}
              ref={ref}
              variant={button?.variant ?? "none"}
              disabled={button?.disabled ?? isDisabled}
              className={cn(
                "w-full justify-start text-left font-normal border",
                !selected && "text-muted-foreground",
                inline &&
                  "transition-colors border-none hover:text-foreground hover:bg-ghost",
                inline &&
                  isActive &&
                  "border-azure text-azure hover:text-azure",
                open && "ring-1 ring-azure",
                className,
                button?.className,
              )}
            >
              <LuCalendar className="h-4 w-4" />
              {inline && showValue && selected ? (
                <span>{renderTime(selected)}</span>
              ) : inline && placeholder ? (
                <span>{placeholder}</span>
              ) : props.parseRenderedTime && open ? (
                <DateInput
                  {...input}
                  className={cn("h-6", input?.className)}
                  selected={selected}
                  onSelect={onSelect}
                  renderTime={renderTime}
                  timeZone={props.timeZone}
                  parseRenderedTime={props.parseRenderedTime}
                  placeholder={placeholder ?? "Pick a date"}
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }}
                />
              ) : (
                <span className="pt-[1px]">
                  {selected
                    ? renderTime(selected)
                    : (placeholder ?? "Pick a date")}
                </span>
              )}
              {children}
              {clearable && selected && (
                <LuX
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onSelect(undefined, new Date(), {}, ev);
                  }}
                  className={cn(
                    "ml-auto h-3.5 w-3.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-ghost",
                  )}
                />
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 border-muted" align="start">
          <div className="grid grid-cols-[1fr_auto]">
            <Calendar
              autoFocus
              defaultMonth={defaultMonth}
              {...props}
              selected={selected as Date | undefined}
              onSelect={chainRetainHoursMinutes(onSelect, selected)}
              mode="single"
            />
            {selected && (
              <div className="flex items-center justify-center">
                <TimePicker
                  style={
                    { "--height": "289px", height: "var(--height)" } as any
                  }
                  selected={selected}
                  onSelect={onSelect}
                  timeZone={props.timeZone}
                  withAmPm={withAmPm}
                  intervals={intervals}
                />
              </div>
            )}
            {inline && props.parseRenderedTime && selected && (
              <Button
                {...button}
                ref={ref}
                variant={button?.variant ?? "none"}
                className={cn(
                  "mx-2 mb-1",
                  "w-[calc(100%-1rem)] justify-start text-left items-center font-normal border gap-2",
                  "focus-within:ring-azure focus-within:ring-1",
                  !selected && "text-muted-foreground",
                  // inline &&
                  //   isActive &&
                  //   "border-azure bg-azure/5 text-azure hover:bg-azure/10",
                  // inline && !isActive && "border-aqua hover:bg-aqua/5",
                  // open && "ring-1 ring-azure",
                  button?.className,
                )}
              >
                <LuCalendar className="h-4 w-4" />
                <DateInput
                  {...input}
                  className={cn("pl-1 pr-0 h-6", input?.className)}
                  selected={selected}
                  onSelect={onSelect}
                  renderTime={renderTime}
                  timeZone={props.timeZone}
                  parseRenderedTime={props.parseRenderedTime}
                  placeholder={placeholder ?? "Pick a date"}
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }}
                  onKeyUp={(ev) => {
                    if (ev.key == "Space") {
                      ev.preventDefault();
                      ev.stopPropagation();
                    }
                  }}
                />
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);

export const InlineDatePicker = React.forwardRef<
  HTMLButtonElement,
  Omit<DatePickerProps, "selected" | "onSelect"> & {
    onSave: (date?: Date | null | undefined) => Promise<any> | any;
  }
>(
  (
    {
      className,
      // selected: _selected,
      // onSelect: _onSelect,
      value: _initialValue,
      // onChange,
      renderTime = defaultRenderType,
      children,
      defaultMonth: _defaultMonth,

      button,
      input,

      // time-picker
      intervals,
      withAmPm,

      onSave,
      isDisabled,
      clearable,
      placeholder,
      onClose,
      ...props
    },
    ref,
  ) => {
    const [open, _setOpen] = React.useState(false);
    const setOpen: typeof _setOpen = React.useCallback(
      (a) =>
        _setOpen((open) => {
          const next = functionalUpdate(a, open);
          if (!next) {
            if (onClose) onClose();
          }
          return next;
        }),

      [onClose, _setOpen],
    );
    const initialValue = React.useMemo(
      () =>
        _initialValue && props?.timeZone
          ? new TZDateMini(_initialValue, props.timeZone)
          : _initialValue,
      [_initialValue, props.timeZone],
    );
    const [value, setValue] = React.useState(initialValue);
    const [_, setTemp] = React.useState(initialValue);
    const defaultMonth = _defaultMonth ?? (value || undefined);

    React.useEffect(() => {
      if (!open) {
        setValue(initialValue);
        setTemp(initialValue);
      }
    }, [initialValue]);

    const onSelect: OnSelectHandler<Date | null | undefined> = (date) => {
      setValue(date);
    };

    const display = open ? value : initialValue;

    return (
      <Popover
        open={isDisabled ? false : open}
        onOpenChange={isDisabled ? undefined : setOpen}
      >
        <PopoverTrigger disabled={isDisabled} asChild>
          {isDisabled ? (
            <span className={cn("pt-[1px]", className)}>
              {display ? renderTime(display) : (placeholder ?? "Pick a date")}
            </span>
          ) : (
            <Button
              {...button}
              ref={ref}
              variant={button?.variant ?? "outline"}
              disabled={isDisabled}
              className={cn(
                "w-full justify-start text-left font-normal flex items-center",
                !value && "text-muted-foreground",
                className,
                button?.className,
              )}
            >
              <LuCalendar className="h-4 w-4" />
              {props.parseRenderedTime && open ? (
                <DateInput
                  {...input}
                  className={cn("pl-1 pr-0 h-6", input?.className)}
                  selected={value}
                  onSelect={onSelect}
                  renderTime={renderTime}
                  timeZone={props.timeZone}
                  parseRenderedTime={props.parseRenderedTime}
                  placeholder={placeholder ?? "Pick a date"}
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }}
                  onKeyUp={(ev) => {
                    if (ev.key == "Space") {
                      ev.preventDefault();
                      ev.stopPropagation();
                    }
                  }}
                />
              ) : (
                <span className="pl-1 pt-[1px]">
                  {display
                    ? renderTime(display)
                    : (placeholder ?? "Pick a date")}
                  {!open && value !== initialValue ? " ..." : ""}
                </span>
              )}
              {children}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 border-muted" align="start">
          <div>
            <div className="grid grid-cols-[1fr_auto]">
              <Calendar
                autoFocus
                defaultMonth={defaultMonth}
                {...props}
                selected={value as Date | undefined}
                onSelect={chainRetainHoursMinutes(onSelect, value)}
                mode="single"
              />
              {value && (
                <div className="flex items-center justify-center">
                  <TimePicker
                    style={
                      { "--height": "289px", height: "var(--height)" } as any
                    }
                    selected={value || undefined}
                    onSelect={onSelect}
                    timeZone={props.timeZone}
                    withAmPm={withAmPm}
                    intervals={intervals}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center border-muted border-t-2 px-1 py-1">
              {clearable && isDefined(value) && (
                <Button onClick={() => setValue(null)}>{"Clear"}</Button>
              )}
              <div className="ml-auto flex flex-row-reverse align-center gap-2">
                <Button
                  variant={"azure"}
                  disabled={!clearable && !isDefined(value)}
                  onClick={async () => {
                    await onSave(value);
                    setOpen(false);
                  }}
                >
                  {"Done"}
                </Button>
                <Button
                  onClick={() => {
                    setOpen(false);
                    setValue(initialValue);
                  }}
                >
                  {"Cancel"}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
